<template>
  <div class="h-full flex overflow-hidden">
    <!-- 左侧导航栏 -->
    <OpsSideNav :active-tab-id="activeTabId" @open-tab="handleOpenTab" />

    <!-- 右侧内容区域 -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- 顶部标签页栏 -->
      <div class="flex-shrink-0 bg-slate-900/60 backdrop-blur-md border-b border-slate-700/60 overflow-x-auto custom-scrollbar">
        <div class="flex items-center h-11 min-w-max px-2">
          <transition-group
            name="tab-list"
            tag="div"
            class="flex items-center relative"
            move-class="tab-list-move"
          >
            <div
              v-for="tab in openTabs"
              :key="tab.id"
              @click="activeTabId = tab.id"
              class="group flex items-center gap-2 px-4 h-9 mx-1 rounded-md cursor-pointer transition-all duration-200 min-w-[140px] max-w-[220px] border border-transparent tab-item"
              :class="activeTabId === tab.id 
                ? 'bg-slate-800/90 text-blue-300 border-slate-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]' 
                : 'bg-slate-900/60 text-slate-400 hover:bg-slate-800/70 hover:text-white hover:border-slate-600/80'"
            >
              <i :class="[tab.icon, 'text-sm flex-shrink-0', activeTabId === tab.id ? 'text-blue-400' : 'text-slate-500 group-hover:text-blue-300']"></i>
              <span class="text-sm truncate flex-1">{{ tab.label }}</span>
              <button
                @click.stop="closeTab(tab.id)"
                class="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-slate-700 rounded flex-shrink-0 text-slate-400 hover:text-slate-100 ml-2"
                :class="activeTabId === tab.id ? 'opacity-100' : ''"
              >
                <i class="ph-bold ph-x text-xs"></i>
              </button>
            </div>
          </transition-group>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="flex-1 overflow-y-auto p-6">
        <template v-if="currentComponent">
          <transition name="fade-slide" mode="out-in">
            <component
              :is="currentComponent"
              :key="activeTabId"
              v-bind="componentProps"
              @load="handleComponentLoad"
              @update:search-keyword="userSearchKeyword = $event"
              @update:search-role="userSearchRole = $event"
              @update:filter-resolved="fileFilterResolved = $event"
              @update-user="updateUser"
              @delete-user="deleteUser"
              @mark-resolved="markFileResolved"
              @toggle-resolved="toggleFileResolved"
            @update:new-op="(val) => newOp.value = val"
            @create="handleCreateOp"
              @refresh="handleRefresh"
              @page-change="handlePageChange"
            @status-change="handleFeedbackStatusChange"
            @delete="handleFeedbackDelete"
            :error-message="createOpError"
            />
          </transition>
        </template>
        <div v-else class="h-full flex items-center justify-center text-slate-400">
          <div class="text-center">
            <i class="ph-bold ph-folder-open text-6xl mb-4 opacity-50"></i>
            <p class="text-lg">选择一个功能开始使用</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import OpsSideNav from '@/components/layout/OpsSideNav.vue';
import { useOpsManagement } from '@/composables/useOpsManagement';

// 动态导入组件
import OpsUserManagement from '@/components/pages/ops/OpsUserManagement.vue';
import OpsFileManagement from '@/components/pages/ops/OpsFileManagement.vue';
import OpsOrderManagement from '@/components/pages/ops/OpsOrderManagement.vue';
import OpsFeedbackManagement from '@/components/pages/ops/OpsFeedbackManagement.vue';
import OpsOperationLogs from '@/components/pages/ops/OpsOperationLogs.vue';
import OpsCreateOp from '@/components/pages/ops/OpsCreateOp.vue';
import OpsHttpTcpMonitor from '@/components/pages/ops/OpsHttpTcpMonitor.vue';
import OpsMysqlMonitor from '@/components/pages/ops/OpsMysqlMonitor.vue';
import OpsHostMonitor from '@/components/pages/ops/OpsHostMonitor.vue';

// 组件映射
const componentMap = {
  'OpsUserManagement': OpsUserManagement,
  'OpsFileManagement': OpsFileManagement,
  'OpsOrderManagement': OpsOrderManagement,
  'OpsFeedbackManagement': OpsFeedbackManagement,
  'OpsOperationLogs': OpsOperationLogs,
  'OpsCreateOp': OpsCreateOp,
  'OpsHttpTcpMonitor': OpsHttpTcpMonitor,
  'OpsMysqlMonitor': OpsMysqlMonitor,
  'OpsHostMonitor': OpsHostMonitor,
  // network monitor removed
};

const activeTabId = ref('');
const openTabs = ref([]);

// 使用运维管理 composable
const {
  users,
  userSearchKeyword,
  userSearchRole,
  usersLoading,
  usersPage,
  usersPageSize,
  usersTotal,
  files,
  fileFilterResolved,
  filesLoading,
  filesPage,
  filesPageSize,
  filesTotal,
  feedbacks,
  feedbacksLoading,
  feedbacksPage,
  feedbacksPageSize,
  feedbacksTotal,
  newOp,
  operationLogs,
  logsPage,
  logsPageSize,
  logsTotal,
  hosts,
  onlineUsers,
  accessStats,
  mysqlMonitor,
  diskMonitor,
  memoryMonitor,
  cpuLoad,
  monitorLoading,
  formatFileSize,
  getRoleClass,
  loadUsers,
  updateUser,
  deleteUser,
  loadFiles,
  markFileResolved,
  toggleFileResolved,
  loadFeedbacks,
  updateFeedbackStatus,
  deleteFeedback,
  createOp,
  loadOperationLogs,
  loadMonitorData
} = useOpsManagement();

const createOpError = ref('');

// 当前组件
const currentComponent = computed(() => {
  const currentTab = openTabs.value.find(tab => tab.id === activeTabId.value);
  if (!currentTab) return null;
  const comp = componentMap[currentTab.component];
  return comp || null;
});

// 组件属性
const componentProps = computed(() => {
  const tabId = activeTabId.value;
  
  if (tabId === 'user-management') {
    return {
      users: users.value,
      searchKeyword: userSearchKeyword.value,
      searchRole: userSearchRole.value,
      loading: usersLoading.value,
      getRoleClass: getRoleClass,
      page: usersPage.value,
      pageSize: usersPageSize.value,
      total: usersTotal.value
    };
  } else if (tabId === 'file-management') {
    return {
      files: files.value,
      filterResolved: fileFilterResolved.value,
      loading: filesLoading.value,
      formatFileSize: formatFileSize,
      page: filesPage.value,
      pageSize: filesPageSize.value,
      total: filesTotal.value
    };
  } else if (tabId === 'feedback-management') {
    return {
      feedbacks: feedbacks.value,
      loading: feedbacksLoading.value,
      page: feedbacksPage.value,
      pageSize: feedbacksPageSize.value,
      total: feedbacksTotal.value
    };
  } else if (tabId === 'operation-logs') {
    return {
      logs: operationLogs.value,
      loading: false,
      page: logsPage.value,
      pageSize: logsPageSize.value,
      total: logsTotal.value
    };
  } else if (tabId === 'http-tcp-monitor') {
    return {
      onlineUsers: onlineUsers.value,
      accessStats: accessStats.value,
      loading: monitorLoading.value
    };
  } else if (tabId === 'monitor-overview') {
    return {
      // 可按需补充 overview 所需 props
    };
  } else if (tabId === 'host-monitor') {
    return {
      hosts: hosts.value,
      formatFileSize: formatFileSize,
      loading: monitorLoading.value,
      diskMonitor: diskMonitor.value,
      memoryMonitor: memoryMonitor.value,
      cpuLoad: cpuLoad.value
    };
  } else if (tabId === 'mysql-monitor') {
    return {
      monitorData: mysqlMonitor.value,
      loading: monitorLoading.value,
      formatFileSize: formatFileSize
    };
  } else if (tabId === 'create-op') {
    return {
      newOp: newOp.value,
      errorMessage: createOpError.value
    };
  }
  
  return {};
});

// 打开标签页
const handleOpenTab = (tab) => {
  // 检查是否已打开
  const existingTab = openTabs.value.find(t => t.id === tab.id);
  if (existingTab) {
    activeTabId.value = tab.id;
    return;
  }

  // 仅允许已映射的组件被打开，防止旧数据残留
  if (!componentMap[tab.component]) {
    console.warn('未知的组件标签，已忽略', tab);
    return;
  }
  
  // 添加新标签页
  openTabs.value.push(tab);
  activeTabId.value = tab.id;
  
  // 加载对应数据
  loadTabData(tab.id);
};

// 关闭标签页
const closeTab = (tabId) => {
  const index = openTabs.value.findIndex(t => t.id === tabId);
  if (index === -1) return;
  
  openTabs.value.splice(index, 1);
  
  // 如果关闭的是当前激活的标签页，切换到其他标签页
  if (activeTabId.value === tabId) {
    if (openTabs.value.length > 0) {
      // 优先选择前一个，否则选择第一个
      const newIndex = Math.max(0, index - 1);
      activeTabId.value = openTabs.value[newIndex].id;
    } else {
      activeTabId.value = '';
    }
  }
};

// 加载标签页数据
const loadTabData = (tabId) => {
  if (tabId === 'user-management') {
    loadUsers();
  } else if (tabId === 'file-management') {
    loadFiles();
  } else if (tabId === 'feedback-management') {
    loadFeedbacks();
  } else if (tabId === 'operation-logs') {
    loadOperationLogs(null, null, null, logsPage.value);
  } else if (tabId === 'http-tcp-monitor' ||
             tabId === 'host-monitor' || tabId === 'mysql-monitor' ||
             tabId === 'network-monitor') {
    loadMonitorData();
  }
};

// 处理刷新事件
const handleRefresh = () => {
  const tabId = activeTabId.value;
  if (tabId === 'host-monitor' || tabId === 'mysql-monitor' ||
      tabId === 'network-monitor' || 
      tabId === 'http-tcp-monitor') {
    loadMonitorData();
  }
};

// 处理组件事件
const handleComponentLoad = (params) => {
  const tabId = activeTabId.value;
  if (tabId === 'user-management') {
    loadUsers();
  } else if (tabId === 'file-management') {
    loadFiles();
  } else if (tabId === 'feedback-management') {
    loadFeedbacks();
  } else if (tabId === 'operation-logs') {
    // 搜索或筛选时重置到第一页
    // 处理参数：可能是对象或数组（兼容旧代码）
    let userId = null;
    let action = null;
    
    if (params) {
      // 如果是对象
      if (typeof params === 'object' && !Array.isArray(params)) {
        // 处理用户ID：如果是空字符串或无效，则设为null
        if (params.userId) {
          const userIdStr = String(params.userId).trim();
          userId = userIdStr && !isNaN(userIdStr) ? parseInt(userIdStr) : null;
        }
        // 处理操作类型：如果是空字符串，则设为null
        if (params.action) {
          const actionStr = String(params.action).trim();
          action = actionStr !== '' ? actionStr : null;
        }
      } 
      // 如果是数组（兼容旧代码）
      else if (Array.isArray(params) && params.length >= 2) {
        const userIdStr = params[0];
        userId = userIdStr && !isNaN(userIdStr) ? parseInt(userIdStr) : null;
        const actionStr = params[1];
        action = actionStr && String(actionStr).trim() !== '' ? String(actionStr).trim() : null;
      }
    }
    
    loadOperationLogs(userId, action, null, 1);
  }
};

// 处理分页变化
const handlePageChange = (page) => {
  const tabId = activeTabId.value;
  if (tabId === 'user-management') {
    loadUsers(page);
  } else if (tabId === 'file-management') {
    loadFiles(page);
  } else if (tabId === 'feedback-management') {
    loadFeedbacks(page);
  } else if (tabId === 'operation-logs') {
    loadOperationLogs(null, null, null, page);
  }
};

const handleCreateOp = async () => {
  createOpError.value = '';
  const res = await createOp();
  if (!res?.ok) {
    createOpError.value = res?.message || '创建失败';
  }
};

// 处理反馈状态变更
const handleFeedbackStatusChange = async ({ id, status, adminRemark }) => {
  const res = await updateFeedbackStatus(id, status, adminRemark);
  if (res?.ok) {
    loadFeedbacks(feedbacksPage.value);
  }
};

// 处理反馈删除
const handleFeedbackDelete = async (id) => {
  const res = await deleteFeedback(id);
  if (res?.ok) {
    loadFeedbacks(feedbacksPage.value);
  }
};

// 监听标签页切换
watch(activeTabId, (newTabId) => {
  if (newTabId) {
    loadTabData(newTabId);
  }
});

// 默认打开用户管理
onMounted(() => {
  // 不默认打开任何标签，保持空状态
  activeTabId.value = '';
  openTabs.value = [];
});
</script>

<style scoped>
/* 滚动条样式 */
.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.5);
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.7);
}

/* 标签栏过渡动画 - 挤开和缩回效果 */
.tab-list {
  display: flex;
  align-items: center;
  position: relative;
}

.tab-item {
  flex-shrink: 0;
  will-change: transform, opacity, max-width, min-width, padding, margin;
}

/* 新标签进入动画 - 从右侧滑入并挤开其他标签 */
.tab-list-enter-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: right center;
}

.tab-list-enter-from {
  opacity: 0;
  transform: translateX(30px) scale(0.85);
  max-width: 0 !important;
  min-width: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  overflow: hidden;
}

.tab-list-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* 标签离开动画 - 缩小并消失，其他标签缩回 */
.tab-list-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19);
  position: absolute;
  transform-origin: center;
  z-index: 0;
}

.tab-list-leave-from {
  opacity: 1;
  transform: scale(1);
}

.tab-list-leave-to {
  opacity: 0;
  transform: scale(0.75) translateY(-2px);
  max-width: 0 !important;
  min-width: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  overflow: hidden;
}

/* 移动动画 - 当标签位置变化时，平滑移动（挤开/缩回效果） */
.tab-list-move {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform;
}

/* 页面切换动画 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
